
import React, { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PlusCircle, FileText, ShoppingCart } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import CampaignSetup from '@/components/admin/campaigns/CampaignSetup';
import CampaignTypesSection from '@/components/admin/campaigns/sections/CampaignTypesSection';
import InactiveCampaignsSection from '@/components/admin/campaigns/sections/InactiveCampaignsSection';
import CampaignAnalyticsSection from '@/components/admin/campaigns/sections/CampaignAnalyticsSection';
import CampaignReportsSection from '@/components/admin/campaigns/sections/CampaignReportsSection';
import { campaignTypes } from '@/data/campaignTypes';

const AdminCampaigns = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCampaignType, setSelectedCampaignType] = useState<string | null>(null);
  
  const handleLogout = () => {
    navigate('/login');
  };
  
  const handleCampaignTypeSelect = (type: string) => {
    setSelectedCampaignType(type);
    setActiveTab('setup');
    
    toast({
      title: "Campaign Type Selected",
      description: `You've selected the ${type} campaign type.`,
    });
  };
  
  const handleBackToTypes = () => {
    setSelectedCampaignType(null);
    setActiveTab('overview');
  };

  const handleCreateNewCampaign = () => {
    setActiveTab('overview');
    
    toast({
      title: "Create New Campaign",
      description: "Please select a campaign type to get started.",
    });
  };

  // New: go to ecommerce campaigns page
  const handleEcommerceCampaigns = () => {
    navigate('/admin/ecommerce-campaigns');
  };

  return (
    <AdminLayout
      isLoading={false}
      onLogout={handleLogout}
      userRole="admin"
      activeTab="campaigns"
    >
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">Campaign Management</h1>
            {/* NEW: Add quick access to E-commerce Campaigns page */}
            <Button
              variant="outline"
              className="flex items-center ml-2"
              onClick={handleEcommerceCampaigns}
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              E-commerce Campaigns
            </Button>
          </div>
          <div>
            {!selectedCampaignType && (
              <Button onClick={handleCreateNewCampaign}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Create New Campaign
              </Button>
            )}
            {selectedCampaignType && (
              <Button variant="outline" onClick={handleBackToTypes}>
                Back to Campaign Types
              </Button>
            )}
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Campaign Types</TabsTrigger>
            <TabsTrigger value="setup" disabled={!selectedCampaignType}>Campaign Setup</TabsTrigger>
            <TabsTrigger value="active">Active Campaigns</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="reports">
              <FileText className="h-4 w-4 mr-2" />
              Reports
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <CampaignTypesSection 
              campaignTypes={campaignTypes}
              onSelect={handleCampaignTypeSelect}
            />
          </TabsContent>
          
          <TabsContent value="setup">
            {selectedCampaignType && (
              <CampaignSetup
                campaignType={selectedCampaignType}
                campaignData={campaignTypes.find(c => c.id === selectedCampaignType)}
              />
            )}
          </TabsContent>
          
          <TabsContent value="active">
            <InactiveCampaignsSection />
          </TabsContent>
          
          <TabsContent value="analytics">
            <CampaignAnalyticsSection />
          </TabsContent>
          
          <TabsContent value="reports">
            <CampaignReportsSection />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminCampaigns;
